import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { listSettingDefinitions, createSettingDefinition, deleteSettingDefinition, type SettingDefinition } from '../services/authApi'

export function SettingDefsPage() {
  const [defs, setDefs] = useState<SettingDefinition[]>([])
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setDefs(await listSettingDefinitions())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleDelete(key: string) {
    if (!confirm(`Delete setting "${key}"? This will remove it from all research domains.`)) return
    try {
      await deleteSettingDefinition(key)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  // Group by category
  const categories = new Map<string, SettingDefinition[]>()
  for (const d of defs) {
    const cat = d.category || 'general'
    if (!categories.has(cat)) categories.set(cat, [])
    categories.get(cat)!.push(d)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h3">Setting Definitions</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform-level settings registry. Defines what settings research domain admins can configure.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Setting
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {Array.from(categories.entries()).map(([category, catDefs]) => (
        <Box key={category} sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>{category}</Typography>
          <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Allowed Values</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catDefs.map(d => (
                  <TableRow key={d.key} hover>
                    <TableCell><code style={{ fontSize: '0.75rem' }}>{d.key}</code></TableCell>
                    <TableCell>{d.label}</TableCell>
                    <TableCell><Chip label={d.valueType} size="small" variant="outlined" /></TableCell>
                    <TableCell><code style={{ fontSize: '0.75rem' }}>{d.defaultValue}</code></TableCell>
                    <TableCell>
                      {d.allowedValues ? (
                        <code style={{ fontSize: '0.75rem' }}>{d.allowedValues}</code>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDelete(d.key)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      <CreateSettingDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load() }}
      />
    </Box>
  )
}

function CreateSettingDialog({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void
}) {
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [valueType, setValueType] = useState('string')
  const [defaultValue, setDefaultValue] = useState('')
  const [allowedValues, setAllowedValues] = useState('')
  const [category, setCategory] = useState('general')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleCreate() {
    setError('')
    setLoading(true)
    try {
      const body: Parameters<typeof createSettingDefinition>[0] = {
        key, label, description, valueType, defaultValue, category,
      }
      if (valueType === 'enum' && allowedValues) {
        body.allowedValues = allowedValues.split(',').map(v => v.trim())
      }
      await createSettingDefinition(body)
      setKey(''); setLabel(''); setDescription(''); setDefaultValue(''); setAllowedValues('')
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Setting Definition</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField fullWidth label="Key" value={key} onChange={e => setKey(e.target.value)}
          helperText="lowercase_with_underscores" sx={{ mt: 1, mb: 2 }} />
        <TextField fullWidth label="Label" value={label} onChange={e => setLabel(e.target.value)} sx={{ mb: 2 }} />
        <TextField fullWidth label="Description" value={description} onChange={e => setDescription(e.target.value)} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Value Type</Typography>
            <Select fullWidth size="small" value={valueType} onChange={e => setValueType(e.target.value)}>
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="boolean">Boolean</MenuItem>
              <MenuItem value="enum">Enum</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Category</Typography>
            <Select fullWidth size="small" value={category} onChange={e => setCategory(e.target.value)}>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="visibility">Visibility</MenuItem>
              <MenuItem value="agents">Agents</MenuItem>
              <MenuItem value="onboarding">Onboarding</MenuItem>
              <MenuItem value="security">Security</MenuItem>
            </Select>
          </Box>
        </Box>
        <TextField fullWidth label="Default Value" value={defaultValue} onChange={e => setDefaultValue(e.target.value)} sx={{ mb: 2 }} />
        {valueType === 'enum' && (
          <TextField fullWidth label="Allowed Values" value={allowedValues} onChange={e => setAllowedValues(e.target.value)}
            helperText="Comma-separated: org, private" />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={loading || !key || !label || !defaultValue}>
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
